'use client'

/**
 * Floating WhatsApp click-to-chat button for the public booking site.
 *
 * Rendered once in the booking layout, above the AI chatbot bubble. The
 * prefilled message adapts to where the guest is: property pages send the
 * property's name (and its own number, via `WhatsAppPropertyOverride`), the
 * search page includes the guest's dates and party size, checkout asks for
 * help finishing the booking. Hidden entirely when neither the property nor
 * the tenant has a WhatsApp number configured.
 */

import { usePathname, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useBooking } from '@/contexts/BookingContext'
import { buildWhatsAppUrl } from '@/lib/whatsapp-link'
import { formatDayMonthYear } from '@/lib/date-display'
import { WhatsAppIcon } from '@/lib/social-platforms'
import { useWhatsAppOverride } from './whatsapp-float-store'

export function WhatsAppFloat() {
  const t = useTranslations('booking.whatsapp')
  const locale = useLocale()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const {
    tenantSocialLinks,
    tenantBrandName,
    tenantName,
    propertyName: contextPropertyName,
    isLoading,
  } = useBooking()
  const override = useWhatsAppOverride()

  const rawNumber = override?.whatsapp || tenantSocialLinks?.whatsapp || null
  const brand = tenantBrandName || tenantName || ''
  const propertyName = override?.propertyName || contextPropertyName

  const message = (() => {
    if (pathname.includes('/checkout')) {
      return t('msgCheckout', { brand: propertyName || brand })
    }
    if (propertyName) {
      return t('msgProperty', { property: propertyName })
    }
    if (pathname.includes('/search')) {
      const checkIn = searchParams.get('checkIn')
      const checkOut = searchParams.get('checkOut')
      if (checkIn && checkOut) {
        const adults = parseInt(searchParams.get('adults') || '2', 10)
        const children = parseInt(searchParams.get('children') || '0', 10)
        return t('msgSearch', {
          brand,
          checkIn: formatDayMonthYear(checkIn, locale),
          checkOut: formatDayMonthYear(checkOut, locale),
          guests: adults + children,
        })
      }
    }
    return t('msgGeneric', { brand })
  })()

  const href = buildWhatsAppUrl(rawNumber, message)
  if (isLoading || !href) return null

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('ariaLabel')}
      // dir=ltr pins the [label][bubble] order even on the RTL site — the
      // bubble must hug the right edge in both directions.
      dir="ltr"
      className="group fixed bottom-[9.5rem] right-4 z-40 flex items-center"
    >
      {/* Hover label (desktop) */}
      <span
        className="pointer-events-none me-3 hidden max-w-0 items-center overflow-hidden whitespace-nowrap rounded-full bg-[#128C7E] px-0 py-2 text-sm font-medium text-white opacity-0 shadow-lg transition-all duration-300 group-hover:max-w-xs group-hover:px-4 group-hover:opacity-100 sm:flex"
      >
        {t('tooltip')}
      </span>
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white shadow-lg transition-transform duration-200 group-hover:scale-105">
        <WhatsAppIcon className="h-7 w-7" />
      </span>
    </a>
  )
}
